import { useMemo } from "react";
import { useLocalStorage } from "@/hooks/use-local-storage";

export const ENV_KEY_PREFIX = "environment-settings";

/**
 * Creates a storage key for environment-specific settings.
 *
 * @param environmentId - The unique identifier for the environment.
 * @param key - The specific key for the setting.
 * @returns The composed storage key string.
 */
export function createEnvironmentStorageKey(environmentId: string, key: string): string {
    return `${ENV_KEY_PREFIX}:${environmentId}:${key}`;
}

/**
 * Extracts the environment ID from a storage key.
 *
 * @param key - The storage key string.
 * @returns The extracted environment ID, or null if not found.
 */
export function extractEnvironmentId(key: string): string | null {
    if (!key.startsWith(ENV_KEY_PREFIX)) return null;
    const parts = key.split(":");
    return parts.length >= 2 ? parts[1] : null;
}

/**
 * Checks if a storage key belongs to a specific environment.
 *
 * @param key - The storage key string.
 * @param environmentId - The unique identifier for the environment.
 * @returns True if the key belongs to the environment, false otherwise.
 */
export function isEnvironmentStorageKey(key: string, environmentId: string): boolean {
    return key.startsWith(`${ENV_KEY_PREFIX}:${environmentId}:`);
}

/**
 * A hook to manage environment-specific localStorage settings.
 *
 * It works together with useEnvironmentSettingsCleanup hook that deletes any stale entries
 * that belong to deleted environments.
 *
 * @param environmentId - The unique identifier for the environment.
 * @param key - The specific key for the setting.
 * @param defaultValue - The default value to use if no value is stored.
 * @returns An object containing the current value and a setter function.
 */
export function useEnvironmentSettings(environmentId: string, key: string, defaultValue: string) {
    const storageKey = useMemo(() => createEnvironmentStorageKey(environmentId, key), [environmentId, key]);

    const { storedValue: value, setValue } = useLocalStorage(storageKey, defaultValue);

    return { value, setValue };
}
